import React, { useState, useEffect, useContext, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import axios from '../../../../config/axios'
import ApiService from '../../../../services/ApiService'
import { stripHtml } from '../../../../utils/stripHtml'
import save from '../../../../assets/certificates/AddParameter/save.svg'
import plus from '../../../../assets/productions/plus.svg'
import trash from '../../../../assets/productions/trash.svg'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import {
  MainContainer,
  StyledCard,
  FormGrid,
  StyledFormGroup,
  ActionButtons,
  CancelButton,
  SaveButton,
  SubHeaderContainer,
  SectionName,
  LeftSide,
  BreadcrumbsContainer,
  CardTitle,
  DropdownList,
  DropdownItem,
  AddButton,
  MaterialItemContainer,
  MaterialFields,
  InlineDeleteButton,
  SectionButtons,
  StyledCardHeader,
} from './style'

interface OrderItem {
  id: number
  ploomesProductName?: string
  quantity?: number
  indexInOrder: number
}

interface Order {
  id: number
  saleNumberPloomes: string
  client: string
  items?: OrderItem[]
}

interface MaterialItem {
  id: number
  materialId: number
  material: string
  unitOfMeasure: string
  quantityToUse: string
}

interface ResourceItem {
  id: number
  resourceId: number
  resource: string
  unit: string
  quantity: string
}

interface FormState {
  orderNumber: string
  orderItemId: number
  item: string
  order: string
  client: string
  materials: MaterialItem[]
  resources: ResourceItem[]
}

const INITIAL_FORM_STATE: FormState = {
  orderNumber: '',
  orderItemId: 0,
  item: '',
  order: '',
  client: '',
  materials: [],
  resources: [],
}

const compareSizes = (a: string, b: string): number => {
  const isNumA = /^\d+$/.test(a);
  const isNumB = /^\d+$/.test(b);

  if (isNumA && isNumB) {
    return Number(a) - Number(b);
  }
  if (isNumA && !isNumB) {
    return -1;
  }
  if (!isNumA && isNumB) {
    return 1;
  }

  const textOrder = [
    'PP', 'P', 'M', 'G', 'GG', 'XL', 'XLL',
    'G1', 'G2', 'G3', 'G4',
    'TAMANHO ÚNICO', 'ÚNICO', 'U'
  ];

  const normA = a.trim().toUpperCase();
  const normB = b.trim().toUpperCase();

  const idxA = textOrder.indexOf(normA);
  const idxB = textOrder.indexOf(normB);

  if (idxA !== -1 && idxB !== -1) {
    return idxA - idxB;
  }
  if (idxA !== -1 && idxB === -1) {
    return -1;
  }
  if (idxA === -1 && idxB !== -1) {
    return 1;
  }

  return a.localeCompare(b);
};

const Breadcrumbs: React.FC<{ items: { label: string; path?: string }[] }> = ({ items }) => (
  <BreadcrumbsContainer>
    <ol>
      {items.map((item, index) => (
        <li key={index}>
          {index === items.length - 1 ? (
            <span>{item.label}</span>
          ) : item.path ? (
            <Link to={item.path}>{item.label}</Link>
          ) : (
            <span>{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </BreadcrumbsContainer>
)

const CreateOPFashion: React.FC = () => {
  const navigate = useNavigate()
  const { setFeedback } = useContext(FeedbackContext)

  const [form, setForm] = useState<FormState>(INITIAL_FORM_STATE)
  const [searchOrder, setSearchOrder] = useState('')
  const [searchProduct, setSearchProduct] = useState('')
  const [searchMaterial, setSearchMaterial] = useState('')
  const [searchResource, setSearchResource] = useState('')

  const [isEditingOrder, setIsEditingOrder] = useState(false)
  const [isEditingProduct, setIsEditingProduct] = useState(false)
  const [editingMaterialIndex, setEditingMaterialIndex] = useState<number | null>(null)
  const [editingResourceIndex, setEditingResourceIndex] = useState<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [orderOptions, setOrderOptions] = useState<Order[]>([])
  const [productOptions, setProductOptions] = useState<any[]>([])
  const [materialOptions, setMaterialOptions] = useState<any[]>([])
  const [resourceOptions, setResourceOptions] = useState<any[]>([])

  const [selectedProduct, setSelectedProduct] = useState<any | null>(null)
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)

  const [availableColors, setAvailableColors] = useState<any[]>([])
  const [availableSizes, setAvailableSizes] = useState<string[]>([])
  const [quantities, setQuantities] = useState<{ [colorId: number]: { [size: string]: string } }>({})

  const fetchOrders = useCallback(async (search?: string) => {
    try {
      const response = await ApiService.steelOrders.findOrders(search ? { search } : undefined)
      let orders = []
      if (Array.isArray(response.data)) {
        orders = response.data
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        orders = response.data.data
      }
      setOrderOptions(orders)
    } catch {
      setOrderOptions([])
    }
  }, [])

  const fetchProducts = useCallback(async (search?: string) => {
    const token = localStorage.getItem('token')
    if (!token) return
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product/paginated`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { page: 0, size: 50, search: search || undefined },
        }
      )
      const data = response.data
      let list = []
      if (Array.isArray(data)) {
        list = data
      } else if (data && Array.isArray(data.content)) {
        list = data.content
      }
      setProductOptions(list)
    } catch {
      setProductOptions([])
    }
  }, [])

  const fetchMaterials = useCallback(async (search?: string) => {
    const token = localStorage.getItem('token')
    if (!token) return
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/material/list/stock`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { search: search || undefined },
        }
      )
      setMaterialOptions(Array.isArray(response.data) ? response.data : [])
    } catch {
      setMaterialOptions([])
    }
  }, [])

  const fetchResources = useCallback(async (search?: string) => {
    const token = localStorage.getItem('token')
    if (!token) return
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product/resources`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { page: 0, size: 50, search: search || undefined },
        }
      )
      const data = response.data
      let list = []
      if (Array.isArray(data)) {
        list = data
      } else if (data && Array.isArray(data.content)) {
        list = data.content
      }
      setResourceOptions(list)
    } catch {
      setResourceOptions([])
    }
  }, [])

  useEffect(() => {
    if (isEditingOrder) {
      const timeoutId = setTimeout(() => {
        fetchOrders(searchOrder)
      }, 300)
      return () => clearTimeout(timeoutId)
    }
  }, [isEditingOrder, searchOrder, fetchOrders])

  useEffect(() => {
    if (isEditingProduct) {
      const timeoutId = setTimeout(() => {
        fetchProducts(searchProduct)
      }, 300)
      return () => clearTimeout(timeoutId)
    }
  }, [isEditingProduct, searchProduct, fetchProducts])

  useEffect(() => {
    if (editingMaterialIndex !== null) {
      const timeoutId = setTimeout(() => {
        fetchMaterials(searchMaterial)
      }, 300)
      return () => clearTimeout(timeoutId)
    }
  }, [editingMaterialIndex, searchMaterial, fetchMaterials])

  useEffect(() => {
    if (editingResourceIndex !== null) {
      const timeoutId = setTimeout(() => {
        fetchResources(searchResource)
      }, 300)
      return () => clearTimeout(timeoutId)
    }
  }, [editingResourceIndex, searchResource, fetchResources])

  const selectOrder = (order: Order) => {
    setSelectedOrder(order)
    let matchedItem = order.items?.[0]
    if (selectedProduct && order.items) {
      const match = order.items.find((item) => {
        const itemAny = item as any
        return itemAny.productId === selectedProduct.id
      })
      if (match) matchedItem = match
    }

    setForm((prev) => ({
      ...prev,
      order: order.saleNumberPloomes || order.id.toString(),
      orderItemId: matchedItem?.id || 0,
      item: stripHtml(matchedItem?.ploomesProductName) || 'Item sem descrição',
      client: order.client,
    }))
    setIsEditingOrder(false)
    setSearchOrder('')
  }

  const selectProduct = async (product: any) => {
    setSelectedProduct(product)
    setIsEditingProduct(false)
    setSearchProduct('')

    const token = localStorage.getItem('token')
    if (token) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product/${product.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        const productDetails = response.data

        const colorsList: any[] = []
        if (Array.isArray(productDetails.ProductItemColor)) {
          productDetails.ProductItemColor.forEach((pic: any) => {
            if (pic.CollectionColor) {
              colorsList.push({
                id: pic.CollectionColor.id,
                name: pic.CollectionColor.name,
              })
            }
          })
        }
        setAvailableColors(colorsList)

        const sizesList: string[] = []
        if (Array.isArray(productDetails.GradeInProduct)) {
          productDetails.GradeInProduct.forEach((gp: any) => {
            if (gp.Grade?.name) {
              sizesList.push(gp.Grade.name)
            }
          })
        }
        const sortedSizes = sizesList.sort(compareSizes)
        setAvailableSizes(sortedSizes)

        const initialQuantities: { [colorId: number]: { [size: string]: string } } = {}
        colorsList.forEach((c) => {
          initialQuantities[c.id] = {}
          sortedSizes.forEach((s) => {
            initialQuantities[c.id][s] = '0'
          })
        })
        setQuantities(initialQuantities)

        const script = productDetails?.Script?.[0]
        if (script) {
          const newMaterials: MaterialItem[] = []
          const newResources: ResourceItem[] = []

          if (Array.isArray(script.ScriptMaterial)) {
            script.ScriptMaterial.forEach((sm: any) => {
              if (sm.Material) {
                const name = sm.Material.name || ''
                newMaterials.push({
                  id: Date.now() + Math.random(),
                  materialId: sm.Material.id,
                  material: name,
                  unitOfMeasure: sm.UnitOfMeasurement?.name || 'UN',
                  quantityToUse: sm.quantity?.toString() || '1',
                })
              }
            })
          }

          if (Array.isArray(script.ScriptResource)) {
            script.ScriptResource.forEach((sr: any) => {
              if (sr.Resource) {
                const name = sr.Resource.name || ''
                newResources.push({
                  id: Date.now() + Math.random(),
                  resourceId: sr.Resource.id,
                  resource: name,
                  unit: sr.UnitOfMeasurement?.name || 'UN',
                  quantity: sr.quantity?.toString() || '1',
                })
              }
            })
          }

          setForm((prev) => ({
            ...prev,
            materials: newMaterials,
            resources: newResources,
          }))
        }
      } catch (err) {
        console.error('Erro ao carregar detalhes da ficha técnica:', err)
      }
    }

    if (selectedOrder && selectedOrder.items) {
      const match = selectedOrder.items.find((item) => {
        const itemAny = item as any
        return itemAny.productId === product.id
      })
      const finalItem = match || selectedOrder.items[0]
      if (finalItem) {
        setForm((prev) => ({
          ...prev,
          orderItemId: finalItem.id,
          item: stripHtml(finalItem.ploomesProductName) || 'Item sem descrição',
        }))
      }
    }
  }

  const addMaterial = () => {
    setForm((prev) => ({
      ...prev,
      materials: [
        ...prev.materials,
        {
          id: Date.now() + Math.random(),
          materialId: 0,
          material: '',
          unitOfMeasure: 'UN',
          quantityToUse: '',
        },
      ],
    }))
    setEditingMaterialIndex(form.materials.length)
    setSearchMaterial('')
  }

  const removeMaterial = (id: number) => {
    setForm((prev) => ({
      ...prev,
      materials: prev.materials.filter((m) => m.id !== id),
    }))
  }

  const updateMaterial = (id: number, field: keyof MaterialItem, value: any) => {
    setForm((prev) => ({
      ...prev,
      materials: prev.materials.map((m) => (m.id === id ? { ...m, [field]: value } : m)),
    }))
  }

  const selectMaterialItem = (index: number, option: any) => {
    setForm((prev) => {
      const updated = [...prev.materials]
      updated[index] = {
        ...updated[index],
        materialId: option.id,
        material: option.name || option.description || '',
      }
      return { ...prev, materials: updated }
    })
    setEditingMaterialIndex(null)
    setSearchMaterial('')
  }

  const addResource = () => {
    setForm((prev) => ({
      ...prev,
      resources: [
        ...prev.resources,
        {
          id: Date.now() + Math.random(),
          resourceId: 0,
          resource: '',
          unit: 'UN',
          quantity: '',
        },
      ],
    }))
    setEditingResourceIndex(form.resources.length)
    setSearchResource('')
  }

  const removeResource = (id: number) => {
    setForm((prev) => ({
      ...prev,
      resources: prev.resources.filter((r) => r.id !== id),
    }))
  }

  const updateResource = (id: number, field: keyof ResourceItem, value: any) => {
    setForm((prev) => ({
      ...prev,
      resources: prev.resources.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    }))
  }

  const selectResourceItem = (index: number, option: any) => {
    setForm((prev) => {
      const updated = [...prev.resources]
      updated[index] = {
        ...updated[index],
        resourceId: option.id,
        resource: option.name || option.description || '',
      }
      return { ...prev, resources: updated }
    })
    setEditingResourceIndex(null)
    setSearchResource('')
  }

  const handleCancel = () => {
    navigate(-1)
  }

  const discountItemsAutomatically = async (
    opId: number,
    materials: MaterialItem[],
    resources: ResourceItem[],
  ) => {
    try {
      const itemsToDiscount: any = {}

      if (materials.length > 0) {
        itemsToDiscount.materials = materials
          .filter((m) => m.materialId > 0 && m.quantityToUse)
          .map((item, index) => ({
            id: index + 1,
            material: {
              id: item.materialId,
              name: item.material,
            },
            quantityToUse: parseFloat(item.quantityToUse.replace(',', '.')),
            unitOfMeasure: item.unitOfMeasure,
          }))
      }

      if (resources.length > 0) {
        itemsToDiscount.resources = resources
          .filter((r) => r.resourceId > 0 && r.quantity)
          .map((item, index) => ({
            id: index + 1,
            material: {
              id: item.resourceId,
              name: item.resource,
            },
            quantityToUse: parseFloat(item.quantity.replace(',', '.')),
            unitOfMeasure: item.unit,
          }))
      }

      if (itemsToDiscount.materials?.length > 0 || itemsToDiscount.resources?.length > 0) {
        await ApiService.pcp.discountItems(opId, itemsToDiscount)
      }
      return { success: true }
    } catch (error) {
      console.error('Erro no abatimento automático:', error)
      return { success: false }
    }
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()

    if (!form.orderItemId) {
      setFeedback({ isOpen: true, statusOk: false, message: 'Selecione um pedido.' })
      return
    }

    if (!selectedProduct && form.materials.length === 0 && form.resources.length === 0) {
      setFeedback({ isOpen: true, statusOk: false, message: 'Selecione um produto, tecido ou aviamento.' })
      return
    }

    const fashionGrades: any[] = []
    const colorTotals: { [colorId: number]: number } = {}

    Object.keys(quantities).forEach((colorIdStr) => {
      const colorId = parseInt(colorIdStr, 10)
      colorTotals[colorId] = 0
      Object.keys(quantities[colorId]).forEach((size) => {
        const qtyVal = parseInt(quantities[colorId][size], 10) || 0
        if (qtyVal > 0) {
          fashionGrades.push({
            collectionColorId: colorId,
            size,
            quantity: qtyVal,
          })
          colorTotals[colorId] += qtyVal
        }
      })
    })

    if (selectedProduct && fashionGrades.length === 0) {
      setFeedback({ isOpen: true, statusOk: false, message: 'Selecione as quantidades de grade e cor para o produto.' })
      return
    }

    setIsSubmitting(true)

    try {
      const colorsToProduce = Object.keys(colorTotals)
        .map((k) => parseInt(k, 10))
        .filter((cid) => colorTotals[cid] > 0)

      let mainOpId: number | null = null

      for (let index = 0; index < colorsToProduce.length; index++) {
        const cid = colorsToProduce[index]
        const qty = colorTotals[cid]

        const dataToSend: any = {
          orderItemId: form.orderItemId,
          productId: selectedProduct.id,
          collectionColorId: cid,
          quantity: qty,
        }

        if (index === 0) {
          dataToSend.fashionGrades = fashionGrades
        }

        const response = await ApiService.productionFlow.createService(dataToSend)
        const createdOpId =
          response.data?.id ||
          (typeof response.data === 'string'
            ? parseInt(response.data.match(/OP[\s](\d+)/)?.[1] || '0')
            : 0)

        if (index === 0 && createdOpId) {
          mainOpId = createdOpId
        }
      }

      if (mainOpId && (form.materials.length > 0 || form.resources.length > 0)) {
        await discountItemsAutomatically(mainOpId, form.materials, form.resources)
      }

      setFeedback({
        isOpen: true,
        statusOk: true,
        message: 'Ordens de produção criadas com sucesso para as cores selecionadas.',
      })
      navigate('/panel/production-fashion')
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Erro ao criar ordens de produção.'
      setFeedback({ isOpen: true, statusOk: false, message: msg })
    } finally {
      setIsSubmitting(false)
    }
  }

  const breadcrumbItems = [
    { label: 'Produção', path: '/panel/production-fashion' },
    { label: 'Criar OP' },
  ]

  return (
    <>
      <SubHeaderContainer>
        <LeftSide>
          <SectionName>Criar ordem de produção</SectionName>
        </LeftSide>
        <ActionButtons>
          <CancelButton type="button" onClick={handleCancel}>
            Cancelar
          </CancelButton>
          <SaveButton type="button" onClick={handleSubmit} disabled={isSubmitting}>
            <img src={save} alt="Salvar" />
            {isSubmitting ? 'Criando OPs...' : 'Salvar OP'}
          </SaveButton>
        </ActionButtons>
      </SubHeaderContainer>

      <Breadcrumbs items={breadcrumbItems} />

      <MainContainer>
        <form onSubmit={handleSubmit} style={{ padding: '0 2rem' }}>
          <StyledCard>
            <CardTitle>Geral</CardTitle>
            <FormGrid cols={2}>
              <StyledFormGroup className="dropdown-container">
                <label htmlFor="order">Pedido</label>
                <input
                  type="text"
                  id="order"
                  value={isEditingOrder ? searchOrder : form.order}
                  onChange={(e) => {
                    setSearchOrder(e.target.value)
                    setIsEditingOrder(true)
                  }}
                  onFocus={() => {
                    setIsEditingOrder(true)
                    setSearchOrder(form.order)
                  }}
                  placeholder="Buscar número do pedido..."
                />
                {isEditingOrder && (
                  <DropdownList>
                    {orderOptions.map((order) => (
                      <DropdownItem
                        key={order.id}
                        onClick={() => selectOrder(order)}
                      >
                        {order.saleNumberPloomes ? `Pedido ${order.saleNumberPloomes}` : `Pedido #${order.id}`}
                      </DropdownItem>
                    ))}
                    {orderOptions.length === 0 && (
                      <DropdownItem style={{ pointerEvents: 'none', opacity: 0.6 }}>
                        Nenhum pedido encontrado
                      </DropdownItem>
                    )}
                  </DropdownList>
                )}
              </StyledFormGroup>

              <StyledFormGroup>
                <label htmlFor="client">Cliente</label>
                <input type="text" id="client" value={form.client} readOnly disabled />
              </StyledFormGroup>
            </FormGrid>
          </StyledCard>

          <StyledCard>
            <CardTitle>Produto</CardTitle>
            <FormGrid cols={2}>
              <StyledFormGroup className="dropdown-container">
                <label htmlFor="product">Produto (Ficha Técnica)</label>
                <input
                  type="text"
                  id="product"
                  value={isEditingProduct ? searchProduct : selectedProduct ? `${selectedProduct.code ? `${selectedProduct.code} - ` : ''}${selectedProduct.name}` : ''}
                  onChange={(e) => {
                    const value = e.target.value
                    setSearchProduct(value)
                    setIsEditingProduct(true)

                    if (!value || value.trim() === '') {
                      setSelectedProduct(null)
                      setAvailableColors([])
                      setAvailableSizes([])
                      setQuantities({})
                    }
                  }}
                  onFocus={() => {
                    setIsEditingProduct(true)
                    setSearchProduct(selectedProduct?.name || '')
                  }}
                  placeholder="Buscar produto (ficha técnica)..."
                />
                {isEditingProduct && productOptions.length > 0 && (
                  <DropdownList>
                    {productOptions.map((product, idx) => (
                      <DropdownItem key={idx} onClick={() => selectProduct(product)}>
                        {product.code ? `${product.code} - ` : ''}{product.name}
                      </DropdownItem>
                    ))}
                  </DropdownList>
                )}
                {isEditingProduct && searchProduct && productOptions.length === 0 && (
                  <DropdownList>
                    <DropdownItem style={{ pointerEvents: 'none', opacity: 0.6 }}>
                      Nenhum produto encontrado
                    </DropdownItem>
                  </DropdownList>
                )}
              </StyledFormGroup>
            </FormGrid>

            {selectedProduct && availableColors.length > 0 && availableSizes.length > 0 && (
              <div style={{ marginTop: '20px', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '10px', fontSize: '13px', fontWeight: 'bold', borderBottom: '2px solid #ddd' }}>Cor</th>
                      {availableSizes.map((size) => (
                        <th key={size} style={{ textAlign: 'center', padding: '10px', fontSize: '13px', fontWeight: 'bold', borderBottom: '2px solid #ddd' }}>{size}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {availableColors.map((color) => (
                      <tr key={color.id}>
                        <td style={{ padding: '10px', fontSize: '13px', fontWeight: 'bold', borderBottom: '1px solid #eee' }}>{color.name}</td>
                        {availableSizes.map((size) => (
                          <td key={size} style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                            <input
                              className="inline-input"
                              type="number"
                              min="0"
                              value={quantities[color.id]?.[size] || '0'}
                              onChange={(e) => {
                                const val = e.target.value
                                setQuantities((prev) => ({
                                  ...prev,
                                  [color.id]: {
                                    ...prev[color.id],
                                    [size]: val,
                                  },
                                }))
                              }}
                              style={{ width: '70px', height: '32px', textAlign: 'center', borderRadius: '100px', border: '1px solid #ddd', padding: '4px' }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {selectedProduct && availableColors.length === 0 && (
              <div style={{ marginTop: '12px', color: '#888', fontSize: '13px' }}>
                Nenhuma cor cadastrada para esta ficha técnica.
              </div>
            )}

            {selectedProduct && availableColors.length > 0 && availableSizes.length === 0 && (
              <div style={{ marginTop: '12px', color: '#e67e22', fontSize: '13px' }}>
                Nenhuma grade/tamanho cadastrado para este produto.
              </div>
            )}
          </StyledCard>

          <StyledCard>
            <StyledCardHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <CardTitle style={{ paddingBottom: 0 }}>Materiais</CardTitle>
            </StyledCardHeader>

            {form.materials.map((material, index) => (
              <MaterialItemContainer key={material.id}>
                <MaterialFields>
                  <StyledFormGroup className="dropdown-container">
                    <label>Material (Tecido)</label>
                    <input
                      type="text"
                      value={editingMaterialIndex === index ? searchMaterial : material.material}
                      onChange={(e) => {
                        setSearchMaterial(e.target.value)
                        setEditingMaterialIndex(index)
                      }}
                      onFocus={() => {
                        setEditingMaterialIndex(index)
                        setSearchMaterial(material.material || '')
                      }}
                      placeholder="Buscar material no estoque..."
                    />
                    {editingMaterialIndex === index && materialOptions.length > 0 && (
                      <DropdownList>
                        {materialOptions.map((option) => (
                          <DropdownItem
                            key={option.id}
                            onClick={() => selectMaterialItem(index, option)}
                          >
                            <strong>{option.code}</strong> - {option.name}
                          </DropdownItem>
                        ))}
                      </DropdownList>
                    )}
                  </StyledFormGroup>

                  <StyledFormGroup>
                    <label>Unidade de Medida</label>
                    <select
                      value={material.unitOfMeasure}
                      onChange={(e) => updateMaterial(material.id, 'unitOfMeasure', e.target.value)}
                    >
                      <option value="UN">UN</option>
                      <option value="KG">KG</option>
                      <option value="MM">MM</option>
                      <option value="MT">MT</option>
                    </select>
                  </StyledFormGroup>

                  <StyledFormGroup>
                    <label>Quantidade</label>
                    <input
                      type="number"
                      step="any"
                      min="0.01"
                      value={material.quantityToUse}
                      onChange={(e) => updateMaterial(material.id, 'quantityToUse', e.target.value)}
                      placeholder="Qtd..."
                    />
                  </StyledFormGroup>
                </MaterialFields>

                <InlineDeleteButton type="button" onClick={() => removeMaterial(material.id)}>
                  <img src={trash} alt="Excluir" />
                </InlineDeleteButton>
              </MaterialItemContainer>
            ))}

            <SectionButtons>
              <AddButton type="button" onClick={addMaterial}>
                <img src={plus} alt="Adicionar" />
                Adicionar material
              </AddButton>
            </SectionButtons>
          </StyledCard>

          <StyledCard>
            <StyledCardHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <CardTitle style={{ paddingBottom: 0 }}>Insumos</CardTitle>
            </StyledCardHeader>

            {form.resources.map((resource, index) => (
              <MaterialItemContainer key={resource.id}>
                <MaterialFields>
                  <StyledFormGroup className="dropdown-container">
                    <label>Insumo (Aviamento)</label>
                    <input
                      type="text"
                      value={editingResourceIndex === index ? searchResource : resource.resource}
                      onChange={(e) => {
                        setSearchResource(e.target.value)
                        setEditingResourceIndex(index)
                      }}
                      onFocus={() => {
                        setEditingResourceIndex(index)
                        setSearchResource(resource.resource || '')
                      }}
                      placeholder="Buscar insumo no estoque..."
                    />
                    {editingResourceIndex === index && resourceOptions.length > 0 && (
                      <DropdownList>
                        {resourceOptions.map((option) => (
                          <DropdownItem
                            key={option.id}
                            onClick={() => selectResourceItem(index, option)}
                          >
                            <strong>{option.code}</strong> - {option.name}
                          </DropdownItem>
                        ))}
                      </DropdownList>
                    )}
                  </StyledFormGroup>

                  <StyledFormGroup>
                    <label>Unidade de Medida</label>
                    <select
                      value={resource.unit}
                      onChange={(e) => updateResource(resource.id, 'unit', e.target.value)}
                    >
                      <option value="UN">UN</option>
                      <option value="KG">KG</option>
                      <option value="MM">MM</option>
                      <option value="MT">MT</option>
                    </select>
                  </StyledFormGroup>

                  <StyledFormGroup>
                    <label>Quantidade</label>
                    <input
                      type="number"
                      step="any"
                      min="0.01"
                      value={resource.quantity}
                      onChange={(e) => updateResource(resource.id, 'quantity', e.target.value)}
                      placeholder="Qtd..."
                    />
                  </StyledFormGroup>
                </MaterialFields>

                <InlineDeleteButton type="button" onClick={() => removeResource(resource.id)}>
                  <img src={trash} alt="Excluir" />
                </InlineDeleteButton>
              </MaterialItemContainer>
            ))}

            <SectionButtons>
              <AddButton type="button" onClick={addResource}>
                <img src={plus} alt="Adicionar" />
                Adicionar insumo
              </AddButton>
            </SectionButtons>
          </StyledCard>
        </form>
      </MainContainer>
    </>
  )
}

export default CreateOPFashion
